/**
 * A daily trend line, hand-rolled in SVG.
 *
 * No charting dependency, for a reason specific to this codebase: AGENTS.md forbids
 * a hex literal outside `colors.ts`, and every charting library colours its series
 * by string. `useColor()` exists precisely to resolve a semantic token for SVG and
 * canvas, which inline SVG consumes natively, while a library would mean threading
 * resolved strings through a props API and re-resolving them on every scheme change.
 * The surface is also one line and two axes — `@mui/x-charts` would pull four `d3-*`
 * packages and a spring runtime for that.
 *
 * If axes, brushing and stacked series ever arrive, revisit with
 * `@mui/x-charts@^9`, matched to the installed Material UI major.
 */

import { ThemeColor, useColor, withAlpha } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import { useId } from "react";

/** A fixed user-space box, stretched to the container by preserveAspectRatio. */
const VIEW_WIDTH = 1000;

export interface TrendPoint {
  day: string;
  value: number;
}

export interface TrendChartProps {
  points: TrendPoint[];
  /** Summarises the series for anyone who cannot see it. */
  label: string;
  height?: number;
  testId?: string;
}

export function TrendChart({
  points,
  label,
  height = 220,
  testId,
}: TrendChartProps) {
  const gradientId = useId();
  const stroke = useColor(ThemeColor.AccentText);
  const baseline = useColor(ThemeColor.Border);

  // A flat series still needs a band to sit in, or every point lands on one edge.
  const max = Math.max(1, ...points.map((point) => point.value));
  const step = points.length > 1 ? VIEW_WIDTH / (points.length - 1) : 0;
  const y = (value: number) => height - (value / max) * (height - 8) - 4;

  const line = points
    .map(
      (point, index) =>
        `${index === 0 ? "M" : "L"}${index * step},${y(point.value)}`,
    )
    .join(" ");
  const area = points.length
    ? `${line} L${(points.length - 1) * step},${height} L0,${height} Z`
    : "";

  return (
    <Box
      data-testid={testId}
      sx={{ width: "100%", height, position: "relative" }}
    >
      <svg
        role="img"
        aria-label={label}
        viewBox={`0 0 ${VIEW_WIDTH} ${height}`}
        preserveAspectRatio="none"
        width="100%"
        height={height}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={withAlpha(stroke, 0.24)} />
            <stop offset="100%" stopColor={withAlpha(stroke, 0)} />
          </linearGradient>
        </defs>
        <line
          x1="0"
          y1={height - 1}
          x2={VIEW_WIDTH}
          y2={height - 1}
          stroke={baseline}
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
        {area ? <path d={area} fill={`url(#${gradientId})`} /> : null}
        {points.length > 1 ? (
          <path
            d={line}
            fill="none"
            stroke={stroke}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            // Without this the horizontal stretch smears the stroke into a wedge.
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
      </svg>
    </Box>
  );
}
